/**
 * ============================================================================
 *  menuChapitres.ts — Ouvrir, fermer, choisir : la logique du menu des chapitres
 * ----------------------------------------------------------------------------
 *  Trois déclencheurs ouvrent le même menu — le titre de la bande haute (mode guide),
 *  le bouton « Chapitres » (mode main), « Tous les chapitres » (fin de chapitre) — et
 *  il se comporte pareil de chacun :
 *
 *   - à l'ouverture la voix se met en pause ; à la fermeture elle ne repart que si elle
 *     JOUAIT (pas en pause, pas arrêtée au bout d'une scène explorée) ;
 *   - choisir un AUTRE chapitre le lance depuis sa scène 1, en mode guide, et ferme le
 *     menu : la voix n'a pas à être relancée, le nouveau chapitre le fait ;
 *   - choisir le chapitre courant ferme simplement le menu.
 *
 *  Pure, pour être éprouvée sous `node` ; `useGuide` en tire les effets (pause, lecture,
 *  changement de chapitre).
 * ==========================================================================*/

export type Declencheur = "titre" | "main" | "fin";

export interface EtatMenu {
  ouvert: boolean;
  origine: Declencheur | null;
  /** La voix jouait-elle à l'ouverture (et sa scène n'était-elle pas finie) ? */
  reprendre: boolean;
}

export const menuFerme: EtatMenu = { ouvert: false, origine: null, reprendre: false };

/** Ce qu'il faut faire en plus de changer l'état : le moteur n'est pas ici. */
export type EffetMenu = "pause" | "lecture" | { chapitre: number } | null;

export interface Resultat {
  etat: EtatMenu;
  effet: EffetMenu;
}

export const reprendreALaFermeture = (jouait: boolean, sceneFinie: boolean): boolean => jouait && !sceneFinie;

export function ouvrirMenu(
  etat: EtatMenu,
  origine: Declencheur,
  voix: { jouait: boolean; sceneFinie: boolean },
): Resultat {
  if (etat.ouvert) return { etat, effet: null };
  const reprendre = reprendreALaFermeture(voix.jouait, voix.sceneFinie);
  // Pas de voix à interrompre depuis l'écran de fin, ni si elle est déjà en pause.
  return { etat: { ouvert: true, origine, reprendre }, effet: reprendre ? "pause" : null };
}

export function fermerMenu(etat: EtatMenu): Resultat {
  if (!etat.ouvert) return { etat, effet: null };
  return { etat: menuFerme, effet: etat.reprendre ? "lecture" : null };
}

export function choisirChapitre(etat: EtatMenu, index: number, courant: number): Resultat {
  if (!etat.ouvert) return { etat, effet: null };
  if (index === courant) return fermerMenu(etat);
  return { etat: menuFerme, effet: { chapitre: index } };
}
